/*
 * terms-gate.js
 * Gates checkout behind a "I agree to the terms and conditions" checkbox.
 *
 * Cart (page + drawer): checkbox lives in cart-summary.liquid. Native #checkout
 * button and the accelerated (Shop Pay / PayPal / GPay) buttons are blocked until
 * the box is ticked. Agreement is saved on the cart via /cart/update.js attributes.
 *
 * Product page: the accelerated "Buy Now" buttons render inside a Shopify iframe
 * and cannot be listened to or auto-triggered. A transparent overlay intercepts the
 * click and opens a modal that contains its own checkbox + Checkout button. Ticking
 * enables the button; pressing it adds the current variant to the cart, records the
 * agreement, and redirects straight to /checkout.
 */

const TERMS_ATTRIBUTE = 'Terms and Conditions';
const TERMS_VALUE = 'Accepted';

function updateCartAttribute(accepted) {
  const root = window.Shopify?.routes?.root || '/';
  return fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [TERMS_ATTRIBUTE]: accepted ? TERMS_VALUE : '' } }),
  }).catch(() => {});
}

function showError(gate) {
  const error = gate.querySelector('[data-terms-error]');
  if (error) error.classList.remove('hidden');
}

function hideError(gate) {
  const error = gate.querySelector('[data-terms-error]');
  if (error) error.classList.add('hidden');
}

/* ---------------------------------------------------------------- Cart gate */

// Keep every cart checkbox instance in sync (cart renders twice: page + drawer).
function syncCartCheckboxes(checked) {
  document.querySelectorAll('.js-terms-checkbox').forEach((cb) => {
    cb.checked = checked;
    const gate = cb.closest('.terms-gated');
    if (!gate) return;
    gate.classList.toggle('is-agreed', checked);
    if (checked) hideError(gate);
  });
}

// Change on a cart checkbox toggles state + records the attribute.
document.addEventListener('change', (event) => {
  const checkbox = event.target;
  if (!checkbox.classList || !checkbox.classList.contains('js-terms-checkbox')) return;
  syncCartCheckboxes(checkbox.checked);
  updateCartAttribute(checkbox.checked);
});

// Capture-phase click interception, so it beats the native form submit.
document.addEventListener(
  'click',
  (event) => {
    const gate = event.target.closest('.terms-gated');
    if (!gate || gate.classList.contains('is-agreed')) return;

    const onCheckout = event.target.closest('.cart__checkout-button');
    const onOverlay = event.target.closest('[data-terms-overlay]');
    if (onCheckout || onOverlay) {
      event.preventDefault();
      event.stopPropagation();
      showError(gate);
    }
  },
  true
);

/* ------------------------------------------------------- Product page modal */

function readNumber(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

// Whole-cart fallback: only used if the theme renders no native "Buy it now"
// button (e.g. wallet-only). Adds the current variant and goes to checkout.
async function fallbackCheckout(form, button) {
  const variantId = form.querySelector('input[name="id"]')?.value;
  const quantity = readNumber(form.querySelector('input[name="quantity"]')?.value, 1);
  if (!variantId) return;

  button.setAttribute('disabled', '');
  button.classList.add('is-loading');

  const root = window.Shopify?.routes?.root || '/';
  try {
    await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    await updateCartAttribute(true);
    window.location.href = `${root}checkout`;
  } catch (e) {
    button.removeAttribute('disabled');
    button.classList.remove('is-loading');
  }
}

// Trigger the native, isolated "Buy it now" checkout (single item, existing cart
// untouched) — same behaviour as clicking the button directly.
function directCheckout(button) {
  const modal = button.closest('[data-terms-modal]');
  const sectionId = modal?.dataset.sectionId;
  const form = document.querySelector(`product-form-component[data-section-id="${sectionId}"]`);
  if (!form) return;

  // Best-effort agreement record. Note: the native buy-it-now creates an isolated
  // checkout that ignores the persistent cart, so this attribute lands on the cart,
  // not necessarily on the express order.
  updateCartAttribute(true);

  const nativeButton =
    form.querySelector('.shopify-payment-button__button--unbranded') ||
    form.querySelector('.shopify-payment-button__button') ||
    form.querySelector('.shopify-payment-button button');

  closeModal(modal);

  if (nativeButton) {
    nativeButton.click();
    return;
  }
  fallbackCheckout(form, button);
}

function openModal(modal) {
  if (!modal) return;
  // Reset each time it opens.
  const checkbox = modal.querySelector('[data-terms-modal-checkbox]');
  const checkout = modal.querySelector('[data-terms-modal-checkout]');
  if (checkbox) checkbox.checked = false;
  if (checkout) checkout.setAttribute('disabled', '');
  if (typeof modal.showModal === 'function') modal.showModal();
  else modal.setAttribute('open', '');
}

function closeModal(modal) {
  if (!modal) return;
  if (typeof modal.close === 'function') modal.close();
  else modal.removeAttribute('open');
}

// Add a transparent overlay above each product-page accelerated buy button.
function injectProductOverlays(root) {
  const blocks = (root || document).querySelectorAll(
    'product-form-component .accelerated-checkout-block'
  );
  blocks.forEach((block) => {
    // Presence-based guard: Shopify may re-render the block's innerHTML and wipe
    // a previously injected overlay, so re-add whenever it's missing.
    if (block.querySelector(':scope > .terms-gate__product-overlay')) return;
    block.style.position = 'relative';

    const overlay = document.createElement('span');
    overlay.className = 'terms-gate__product-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const form = block.closest('product-form-component');
      const sectionId = form?.dataset.sectionId;
      openModal(document.querySelector(`[data-terms-modal][data-section-id="${sectionId}"]`));
    });
    block.appendChild(overlay);
  });
}

// Wire modal controls (delegated so re-rendered modals keep working).
document.addEventListener('change', (event) => {
  const checkbox = event.target;
  if (!checkbox.matches?.('[data-terms-modal-checkbox]')) return;
  const modal = checkbox.closest('[data-terms-modal]');
  const checkout = modal?.querySelector('[data-terms-modal-checkout]');
  if (checkout) checkout.toggleAttribute('disabled', !checkbox.checked);
});

document.addEventListener('click', (event) => {
  const checkout = event.target.closest('[data-terms-modal-checkout]');
  if (checkout && !checkout.hasAttribute('disabled')) {
    event.preventDefault();
    directCheckout(checkout);
    return;
  }
  const close = event.target.closest('[data-terms-close]');
  if (close) {
    event.preventDefault();
    closeModal(close.closest('[data-terms-modal]'));
  }
});

/* ------------------------------------------------------------------- Init */

function init() {
  injectProductOverlays(document);
}

if (document.readyState !== 'loading') init();
else document.addEventListener('DOMContentLoaded', init);

// Product buy buttons / cart drawer can re-render; re-inject overlays as needed.
// Debounced with rAF so frequent Shop Pay iframe mutations don't thrash.
let scheduled = false;
const observer = new MutationObserver(() => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    injectProductOverlays(document);
  });
});
observer.observe(document.documentElement, { childList: true, subtree: true });
